import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { checkIsProject, Product } from 'src/@types/basket';
import { BusinessCustomer } from 'src/@types/customer';
import { PaymentMethod } from 'src/@types/paymentMethods';
import { startPurchaseByQuote } from 'src/api/purchaseApi';
import { createOffsetEmissionRecord, createQuoteByCo2, createQuoteByFootprint } from 'src/api/quoteApi';
import useLocalStorage from 'src/hooks/useLocalStorage';
import useMerchant from 'src/hooks/useMerchant';
import { formatNumber } from 'src/utils/numberFormatter';
import { getPaymentProviderForPaymentMethod } from 'src/utils/paymentMethods';
import { DEFAULT_CURRENCY } from 'src/utils/priceFormatter';
import { convertMassToKg } from 'src/utils/unitConverter';

//
//

export type ICartState = {
  cartData: Cart;
  deleteCartItem: (id: string) => void;
  addCartItem: (product: Product) => void;
  checkIfProductInCart: (id: string) => boolean;
  updateCartItem: (id: string, data: any) => void;
  clearCart: () => void;
  //
  quote?: any | null;
  displayedErrorMessage?: any;
  productWithInsufficientStock?: Product;
  checkProductHasInsufficientStock: (product: Product) => Product | undefined;
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  isLoading: boolean;
  postQuoteErrorMessage?: string;
  isOffsetById: boolean;
  clientSecret: any;
  createQuote: any;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (v: PaymentMethod) => void;
  updateOrderDescription: (val: string) => void;
  validateOrderDescription: (input: string) => any;
  orderDescriptionError: string;
  unlinkFootprint: () => void;
  unlinkEmission: () => void;
  removeCustomerAndProgramme: () => void;
  updateCustomer: (v: BusinessCustomer | null) => void;
}

const now = Date.now();
const expiry = now + 86400000; // 24 hours from now

type Cart = { products: Product[], footprintId: string, emissionRecordId: string, orderDescription: string, expiry: number, customer: any, programmeCode: string };
const cartInitialData: Cart = { products: [], footprintId: '', emissionRecordId: '', orderDescription: '', expiry: expiry, customer: {}, programmeCode: '' };

export const CartContext = React.createContext<ICartState>({
  cartData: cartInitialData,
  deleteCartItem: () => {},
  addCartItem: () => {},
  checkIfProductInCart: () => true,
  updateCartItem: () => {},
  clearCart: () => {},
  quote: null,
  displayedErrorMessage: '',
  productWithInsufficientStock: undefined,
  checkProductHasInsufficientStock: () => undefined,
  selectedCurrency: '',
  setSelectedCurrency: () => {},
  isLoading: true,
  postQuoteErrorMessage: undefined,
  isOffsetById: false,
  clientSecret: '',
  createQuote: () => {},
  paymentMethod: 'Card',
  setPaymentMethod: () => {},
  updateOrderDescription: () => {},
  validateOrderDescription: () => {},
  orderDescriptionError: '',
  unlinkFootprint: () => {},
  unlinkEmission: () => {},
  removeCustomerAndProgramme: () => {},
  updateCustomer: () => {},
});

type CartProviderProps = {
  children: React.ReactNode;
};

type PromiseOptions = { cancel: boolean };

type Id = Partial<{ id: string }>;

//

export const CartContextProvider = ({ children }: CartProviderProps) => {
  const { merchant } = useMerchant();

  const [cartData, setCartData] = useLocalStorage<Cart>('cart', cartInitialData);
  const [quote, setQuote] = useState<any | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_CURRENCY);

  const [clientSecret, setClientSecret] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Card');
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [orderDescriptionError, setOrderDescriptionError] = useState('');

  const footprintIdParam = searchParams.get('footprintId');
  const emissionRecordIdParam = searchParams.get('emissionRecordId');

  const { search } = useLocation();
  const mergedParams = new URLSearchParams(search);
  const programmeCodeParam = mergedParams.get('programmeCode');

  useEffect(() => {
    if (programmeCodeParam) {
      setCartData((prev: Cart) => ({ ...prev, programmeCode: programmeCodeParam }));
      unlinkEmission();
      unlinkFootprint();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeCodeParam]);

  useEffect(() => {
    if (footprintIdParam) {
      removeCustomerAndProgramme();
      setCartData((prev: Cart) => ({ ...prev, emissionRecordId: '' }))
      setCartData((prev: Cart) => ({ ...prev, footprintId: footprintIdParam }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footprintIdParam]);

  useEffect(() => {
    if (emissionRecordIdParam) {
      removeCustomerAndProgramme();
      setCartData((prev: Cart) => ({ ...prev, footprintId: '' }))
      setCartData((prev: Cart) => ({ ...prev, emissionRecordId: emissionRecordIdParam }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionRecordIdParam]);

  const footprintId = cartData?.footprintId;
  const emissionRecordId = cartData?.emissionRecordId;

  const isOffsetById = !!footprintId || !!emissionRecordId;

  useEffect(() => {
    if (merchant?.currency) {
      setSelectedCurrency(merchant.currency);
    }
  }, [merchant?.currency]);

  const totalCo2Amount = useMemo(() => {
    return cartData?.products?.reduce(
      (acc: any, curr: Product) =>
        ((curr.selectedAmount && +convertMassToKg(curr.selectedAmount)) || 0) + acc,
      0
    );
  }, [cartData?.products]);

  const postQuoteErrorMessage =
    !!totalCo2Amount &&
    !!quote &&
    quote?.warnings &&
    quote.warnings.some((w: any) => w.code === 'INSUFFICIENT_INVENTORY') &&
    "Unfortunately, we don't have that much of stock left for these products. Consider buying a lower amount of of CO₂.";

  //

  const deleteCartItem = (id: string) => {
    if (id) {
      setCartData((prev: Cart) => ({ ...prev, products: prev.products?.filter(p => p.id !== id) }))
    }
  }

  const addCartItem = (product: Product) => {
    if (!product) return;

    const isDuplicated = cartData?.products.find((item: Product) => item.id === product.id);
    if (!isDuplicated) {
      const resultingCartList = [...cartData?.products, product].slice(-4); // 4 last items
      const uniqueProductTypesCount = resultingCartList.map(checkIsProject).filter((v,i,a) => a.indexOf(v) === i).length;
      const cartListWithOneProductType = (uniqueProductTypesCount > 1 || !checkIsProject(product) ) ? [product] : resultingCartList;
      setCartData((prev: Cart) => ({ ...prev, products: cartListWithOneProductType }));
    }
  }

  const clearCart = () => {
    setCartData(cartInitialData);
  };

  const checkIfProductInCart = (id: string) => {
    return !!cartData?.products?.find((item: Id) => item.id === id);
  }

  const updateCartItem = (id: string, data: any) => {
    if (!id) {
      return;
    }

    const newArr = cartData?.products.map((el: any) => {
      if (el?.id === id) {
        return {
          ...el,
          ...data, 
        }
      }

      return el;
    })

    setCartData((prev: Cart) => ({ ...prev, products: newArr }));
  }

  const updateOrderDescription = (value: string) => {
    setCartData((prev: Cart) => ({ ...prev, orderDescription: value }));
  }

  const updateCustomer = (customer: BusinessCustomer | null) => {
    setCartData((prev: Cart) => ({ ...prev, customer: customer }));
  }

  const minimumCO2Amount = 250;
  const maximumCO2Amount = 500000; // in KG

  const productWithInsufficientStock = (cartData?.products as Product[])
    ?.filter((e) => e?.maximumAvailableForPurchase && e?.selectedAmount?.unit)
    .find((e) => convertMassToKg(e!.maximumAvailableForPurchase) < convertMassToKg(e!.selectedAmount!));

  const checkProductHasInsufficientStock = (product: Product) => {
    if (!product) return;

    return convertMassToKg(product!.maximumAvailableForPurchase) < convertMassToKg(product!.selectedAmount!) ? product : undefined;
  }

  const inputValidationErrorMessage = !totalCo2Amount
    ? null
    : totalCo2Amount < minimumCO2Amount
    ? `Sorry, the minimum purchase amount is ${formatNumber(minimumCO2Amount)} kg of CO₂.`
    : totalCo2Amount > maximumCO2Amount
    ? `Sorry, the maximum purchase amount is ${formatNumber(maximumCO2Amount)} kg. Please contact us for a custom quote.`
    : productWithInsufficientStock
    ? `Your order could not be fulfilled due to insufficient stock. Please review your selection below or enquire about a custom order. `
    : null;

  const displayedErrorMessage = inputValidationErrorMessage || postQuoteErrorMessage || null;

  const validateOrderDescription = (input: string) => {
    if (input?.length > 200) {
      setOrderDescriptionError('Maximum length is 200 characters');
    } else {
      setOrderDescriptionError('');
    }
  }

  const createQuoteForBasket = useCallback(async (quoteResult: AxiosResponse<any, any>) => {
    if (!selectedCurrency) return;

    if (quoteResult?.data?.quoteId) {
      setQuote(quoteResult?.data);

      const startPurchaseByQuoteResult = await startPurchaseByQuote({
        quoteId: quoteResult.data.quoteId,
      });

      setClientSecret(startPurchaseByQuoteResult?.data?.clientSecret);
    }
  },[selectedCurrency]);

  const createQuoteForProject = async (quoteResult: AxiosResponse<any, any>) => {
    if (quoteResult?.data?.quoteId) {
      setQuote(quoteResult?.data);

      const startPurchaseByQuoteResult = await startPurchaseByQuote({
        quoteId: quoteResult?.data?.quoteId,
      });

      setClientSecret(startPurchaseByQuoteResult?.data?.clientSecret);
    }
  };

  const createQuote = useCallback(async (
    merchantCode: string,
    currency: string = selectedCurrency,
    paymentMethod: PaymentMethod = 'Card'
  ): Promise<{ isProjects: boolean; quote: AxiosResponse<any, any> } | undefined> => {
    if (
      cartData?.products?.length &&
      (footprintId || emissionRecordId ||
        (cartData?.products?.every((p: any) => p.selectedAmount) && merchant?.code))
    ) {
      const allAreProjects = cartData?.products?.every((p: any) => checkIsProject(p));
      const allAreBaskets = cartData?.products?.every((p: any) => !checkIsProject(p));

      if (!allAreProjects && !allAreBaskets) {
        throw new Error("Can't have both portfolios and separate projects in the cart");
      }
      if (allAreBaskets && cartData?.products?.length > 1) {
        throw new Error('Only one portfolio can be in the cart');
      }

      const paymentProvider = getPaymentProviderForPaymentMethod(paymentMethod);

      if (emissionRecordId) {
        return {
          isProjects: false,
          quote: await createOffsetEmissionRecord({
            merchantCode: merchantCode,
            currency: currency,
            paymentProvider,
            basketCode: cartData?.products?.[0]?.code,
            emissionRecordId,
            warnOnLowInventory: true,
          }),
        };
      } else if (footprintId) {
        return {
          isProjects: false,
          // NOTE: works only for single(?) basket
          quote: await createQuoteByFootprint({
            merchantCode: merchantCode,
            currency: currency,
            paymentProvider,
            basketCode: cartData?.products?.[0]?.code,
            footprintId: footprintId,
          }),
        };
      } else if (allAreProjects) {
        return {
          isProjects: true,
          quote: await createQuoteByCo2({
            merchantCode: merchantCode,
            currency: currency,
            co2Quantity: cartData?.products?.map((p: any) => p.selectedAmount!).map(convertMassToKg)?.reduce((a: any, b: any) => +a + +b, 0),
            paymentProvider,
            basket: {
              portionedItems: cartData?.products?.map((p: any) => ({
                projectCode: p.code,
                co2amountKg: convertMassToKg(p.selectedAmount!),
              })),
            },
          }),
        };
      } else {
        return {
          isProjects: false,
          quote: await createQuoteByCo2({
            merchantCode: merchantCode,
            currency: currency,
            co2Quantity: convertMassToKg(cartData?.products?.[0]!.selectedAmount!),
            paymentProvider,
            basketCode: cartData?.products?.[0]?.code,
          }),
        };
      }
    }
  }, [cartData?.products, merchant, footprintId, emissionRecordId, selectedCurrency]);

  useEffect(() => {
    if (!merchant?.code || !selectedCurrency) return;

    if (footprintId || emissionRecordId) {
      createQuote(merchant?.code, selectedCurrency).then((data) => {
        if (data?.quote) {
          createQuoteForBasket(data?.quote);
        }
      });
    }
  }, [createQuote, selectedCurrency, merchant, footprintId, emissionRecordId, createQuoteForBasket, paymentMethod]);

  const onOffsetClick = useCallback(async (currency: string, paymentMethod: PaymentMethod = 'Card', promiseOptions?: PromiseOptions) => {
    if (!merchant?.code || !currency) return;

    try {
      const data = await createQuote(merchant?.code, currency, paymentMethod);
      const quote = data?.quote;
      const isProject = data?.isProjects;
      if (promiseOptions && promiseOptions.cancel) return;

      if (quote) {
        if (!isProject) {
          await createQuoteForBasket(quote);
        } else {
          await createQuoteForProject(quote);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?.code, createQuote]);

  useEffect(() => {
    const promiseOptions = { cancel: false }
    setQuote(null);
    let newTimeout: number | undefined = undefined;
    
    if (cartData?.products?.every((i: any) => i.selectedAmount && i.selectedAmount.value) && selectedCurrency) {
      setIsLoading(true);
      newTimeout = window.setTimeout(() => onOffsetClick(selectedCurrency, paymentMethod, promiseOptions), 500);
    } else {
      setIsLoading(false)
    }
    return () => { clearTimeout(newTimeout); promiseOptions.cancel = true; }
  }, [cartData?.products, selectedCurrency, inputValidationErrorMessage, onOffsetClick, paymentMethod]);

  //

  const unlinkFootprint = () => {
    setCartData((prev: Cart) => ({ ...prev, footprintId: '' }));
  }

  const unlinkEmission = () => {
    setCartData((prev: Cart) => ({ ...prev, emissionRecordId: '' }));
  }

  const removeCustomerAndProgramme = () => {
    setCartData((prev: Cart) => ({ ...prev, customer: '' }));
    setCartData((prev: Cart) => ({ ...prev, programmeCode: '' }));
  }

  return (
    <CartContext.Provider
      value={{
        cartData,
        deleteCartItem,
        addCartItem,
        checkIfProductInCart,
        updateCartItem,
        clearCart,
        //
        quote,
        displayedErrorMessage,
        productWithInsufficientStock,
        checkProductHasInsufficientStock,
        selectedCurrency,
        setSelectedCurrency,
        isLoading,
        postQuoteErrorMessage,
        isOffsetById,
        clientSecret,
        createQuote,
        paymentMethod,
        setPaymentMethod,

        updateOrderDescription,
        validateOrderDescription,
        orderDescriptionError,

        unlinkFootprint,
        unlinkEmission,
        removeCustomerAndProgramme,

        updateCustomer,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}